import React, { useState } from 'react';
import * as Papa from 'papaparse';
import * as XLSX from 'xlsx';

const ImportLogsModal = ({
  show,
  onHide,
  onImport,
  canImportAttendance,
  error,
  setError
}) => {
  const [logsCsvData, setLogsCsvData] = useState([]);
  const [logsImportProgress, setLogsImportProgress] = useState(0);
  const [isLogsImporting, setIsLogsImporting] = useState(false);

  const downloadLogsTemplate = () => {
    // Create sample data for attendance logs template with DD/MM/YYYY format
    const templateData = [
      {
        emp_code: 'EMP001',
        device_sn: 'DEV001',
        punch_time: '15/01/2024 09:00:00',
        punch_type: '0',
        created_at: '15/01/2024 09:00:00'
      },
      {
        emp_code: 'EMP001',
        device_sn: 'DEV001',
        punch_time: '15/01/2024 13:00:00',
        punch_type: '1',
        created_at: '15/01/2024 13:00:00'
      },
      {
        emp_code: 'EMP002',
        device_sn: 'DEV002',
        punch_time: '15/01/2024 09:15:00',
        punch_type: '0',
        created_at: '15/01/2024 09:15:00'
      },
      {
        emp_code: 'EMP002',
        device_sn: 'DEV002',
        punch_time: '15/01/2024 17:30:00',
        punch_type: '1',
        created_at: '15/01/2024 17:30:00'
      },
      {
        emp_code: 'EMP003',
        device_sn: 'DEV001',
        punch_time: '16/01/2024 08:45:00',
        punch_type: '0',
        created_at: '16/01/2024 08:45:00'
      },
      {
        emp_code: 'EMP003',
        device_sn: 'DEV001',
        punch_time: '16/01/2024 12:30:00',
        punch_type: '2',
        created_at: '16/01/2024 12:30:00'
      },
      {
        emp_code: 'EMP003',
        device_sn: 'DEV001',
        punch_time: '16/01/2024 13:30:00',
        punch_type: '3',
        created_at: '16/01/2024 13:30:00'
      },
      {
        emp_code: 'EMP003',
        device_sn: 'DEV001',
        punch_time: '16/01/2024 17:45:00',
        punch_type: '1',
        created_at: '16/01/2024 17:45:00'
      }
    ];

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(templateData);

    // Set column widths for better readability
    const wscols = [
      { wch: 10 }, // emp_code
      { wch: 12 }, // device_sn
      { wch: 20 }, // punch_time
      { wch: 12 }, // punch_type
      { wch: 20 }  // created_at
    ];
    ws['!cols'] = wscols;

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendance Logs");

    // Add instructions sheet
    const instructions = [
      ['ATTENDANCE LOGS IMPORT TEMPLATE - INSTRUCTIONS'],
      [''],
      ['REQUIRED FIELDS:'],
      ['emp_code - Employee code (must exist in system)'],
      ['punch_time - Punch date and time in DD/MM/YYYY HH:MM:SS format'],
      [''],
      ['OPTIONAL FIELDS:'],
      ['device_sn - Device serial number'],
      ['punch_type - 0=Check-in, 1=Check-out, 2=Break-out, 3=Break-in'],
      ['created_at - Log creation timestamp (auto-filled if empty)'],
      [''],
      ['PUNCH TYPE CODES:'],
      ['0 - Check-in (Morning/Start of shift)'],
      ['1 - Check-out (Evening/End of shift)'],
      ['2 - Break-out (Start of break)'],
      ['3 - Break-in (End of break)'],
      [''],
      ['DATE TIME FORMAT:'],
      ['• Date: DD/MM/YYYY (e.g., 15/01/2024)'],
      ['• Time: HH:MM:SS 24-hour format (e.g., 09:00:00)'],
      ['• Combined: DD/MM/YYYY HH:MM:SS (e.g., 15/01/2024 09:00:00)'],
      [''],
      ['EXAMPLES:'],
      ['• Morning check-in: 15/01/2024 09:00:00'],
      ['• Lunch break out: 15/01/2024 13:00:00'],
      ['• Lunch break in: 15/01/2024 14:00:00'],
      ['• Evening check-out: 15/01/2024 17:30:00'],
      [''],
      ['IMPORTANT NOTES:'],
      ['1. Remove this instructions sheet before uploading'],
      ['2. Keep only the data sheet with header row'],
      ['3. Date and time must be in DD/MM/YYYY HH:MM:SS format'],
      ['4. Employee codes must match existing employees'],
      ['5. Logs will be processed to create attendance records'],
      ['6. Duplicate logs (same emp_code + punch_time + device_sn) will be skipped'],
      [''],
      ['PROCESSING LOGIC:'],
      ['• Check-in and Check-out pairs create attendance records'],
      ['• Work hours are calculated automatically'],
      ['• Multiple punches per day are supported'],
      ['• System handles breaks and overtime automatically'],
      ['• Breaks (types 2 & 3) are deducted from total work hours']
    ];

    const ws2 = XLSX.utils.aoa_to_sheet(instructions);
    const instructionCols = [
      { wch: 70 } // Wide column for instructions
    ];
    ws2['!cols'] = instructionCols;
    XLSX.utils.book_append_sheet(wb, ws2, "Instructions");

    // Add validation sheet
    const validation = [
      ['DATA VALIDATION RULES'],
      [''],
      ['Field', 'Format', 'Required', 'Example'],
      ['emp_code', 'Text', 'Yes', 'EMP001'],
      ['device_sn', 'Text', 'No', 'DEV001'],
      ['punch_time', 'DD/MM/YYYY HH:MM:SS', 'Yes', '15/01/2024 09:00:00'],
      ['punch_type', '0/1/2/3', 'No', '0'],
      ['created_at', 'DD/MM/YYYY HH:MM:SS', 'No', '15/01/2024 09:00:00'],
      [''],
      ['VALID PUNCH TYPES:'],
      ['Code', 'Description', 'Action'],
      ['0', 'Check-in', 'Start work'],
      ['1', 'Check-out', 'End work'],
      ['2', 'Break-out', 'Start break'],
      ['3', 'Break-in', 'End break']
    ];

    const ws3 = XLSX.utils.aoa_to_sheet(validation);
    const validationCols = [
      { wch: 20 },
      { wch: 25 },
      { wch: 15 },
      { wch: 20 }
    ];
    ws3['!cols'] = validationCols;
    XLSX.utils.book_append_sheet(wb, ws3, "Validation Rules");

    // Save file
    XLSX.writeFile(wb, 'attendance_logs_template.xlsx');
  };

  const handleLogsFileUpload = (event) => {
    if (!canImportAttendance()) {
      alert('You do not have permission to import attendance logs');
      return;
    }

    const file = event.target.files[0];
    if (!file) return;

    // Check file extension
    const fileName = file.name.toLowerCase();
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

    if (isExcel) {
      // Handle Excel files
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });

          // Get first sheet
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];

          // Convert to JSON with date handling
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, {
            raw: false, // Get formatted values
            dateNF: 'dd/mm/yyyy hh:mm:ss' // Date-time format
          });

          if (jsonData && jsonData.length > 0) {
            setLogsCsvData(jsonData);
          } else {
            setError('No valid data found in Excel file');
          }
        } catch (error) {
          setError('Error reading Excel file: ' + error.message);
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      // Handle CSV files
      Papa.parse(file, {
        header: true,
        complete: (results) => {
          if (results.data && results.data.length > 0) {
            setLogsCsvData(results.data);
          } else {
            setError('No valid data found in CSV file');
          }
        },
        error: (error) => {
          setError('Error parsing CSV file: ' + error.message);
        }
      });
    }
  };

  const handleLogsImport = async () => {
    if (!canImportAttendance()) {
      alert('You do not have permission to import attendance logs');
      return;
    }

    if (!logsCsvData.length) {
      setError('No data to import');
      return;
    }

    setIsLogsImporting(true);
    setLogsImportProgress(0);
    setError('');

    try {
      await onImport(logsCsvData, setLogsImportProgress);
      onHide();
      setLogsCsvData([]);
    } catch (error) {
      setError('Import failed: ' + error.message);
    } finally {
      setIsLogsImporting(false);
      setLogsImportProgress(100);
    }
  };

  const handleClose = () => {
    setLogsCsvData([]);
    setLogsImportProgress(0);
    setIsLogsImporting(false);
    onHide();
  };

  if (!show) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content border-0 shadow">
          <div className="modal-header bg-warning text-dark">
            <h5 className="modal-title">
              <i className="bi bi-device-hdd me-2"></i>
              Import Attendance Logs from CSV/Excel
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              disabled={isLogsImporting}
            ></button>
          </div>

          <div className="modal-body">
            <div className="row g-4">
              <div className="col-12">
                <div className="alert alert-info">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="alert-heading">
                        <i className="bi bi-info-circle me-2"></i>
                        Attendance Logs File Format Requirements
                      </h6>
                      <p className="mb-1">Your file should include these columns for raw attendance logs:</p>
                      <ul className="mb-0 small">
                        <li><strong>emp_code</strong> (required) - Employee code</li>
                        <li><strong>device_sn</strong> - Device serial number</li>
                        <li><strong>punch_time</strong> (required) - Date and time in <span className="text-danger">DD/MM/YYYY HH:MM:SS</span> format</li>
                        <li><strong>punch_type</strong> - Punch type (0=Check-in, 1=Check-out, 2=Break-out, 3=Break-in)</li>
                        <li><strong>created_at</strong> - Log creation timestamp</li>
                      </ul>
                    </div>
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={downloadLogsTemplate}
                      title="Download Excel Template"
                    >
                      <i className="bi bi-download me-1"></i>
                      Template
                    </button>
                  </div>
                  <div className="mt-2">
                    <small className="text-muted">
                      <i className="bi bi-lightbulb me-1"></i>
                      Tip: This is for importing raw data from biometric/time-clock devices
                    </small>
                  </div>
                </div>
              </div>

              <div className="col-12">
                <div className="card border-warning">
                  <div className="card-header bg-warning text-dark">
                    <h6 className="mb-0">
                      <i className="bi bi-clock-history me-2"></i>
                      Download Logs Template (Important!)
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="row align-items-center">
                      <div className="col-md-8">
                        <h6 className="text-warning">
                          <i className="bi bi-calendar me-2"></i>
                          Use DD/MM/YYYY HH:MM:SS Format
                        </h6>
                        <p className="mb-2">
                          <strong>Step 1:</strong> Download the Excel template with correct datetime format
                        </p>
                        <p className="mb-2">
                          <strong>Step 2:</strong> Fill your data using <span className="text-danger">DD/MM/YYYY HH:MM:SS</span> format
                        </p>
                        <p className="mb-0 text-muted small">
                          <i className="bi bi-exclamation-triangle me-1"></i>
                          Wrong datetime format will cause processing errors
                        </p>
                      </div>
                      <div className="col-md-4 text-end">
                        <button
                          className="btn btn-warning btn-lg"
                          onClick={downloadLogsTemplate}
                        >
                          <i className="bi bi-file-earmark-excel me-2"></i>
                          Download Template
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12">
                <div className="card">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">
                      <i className="bi bi-upload me-2"></i>
                      Upload Your Device Logs File
                    </h6>
                  </div>
                  <div className="card-body">
                    <label className="form-label">Select Device Logs File *</label>
                    <div className="input-group">
                      <input
                        type="file"
                        className="form-control"
                        accept=".csv,.xlsx,.xls"
                        onChange={handleLogsFileUpload}
                        disabled={isLogsImporting}
                      />
                      <button
                        className="btn btn-outline-warning"
                        type="button"
                        onClick={downloadLogsTemplate}
                        title="Download Template"
                        disabled={isLogsImporting}
                      >
                        <i className="bi bi-download me-1"></i>
                        Template
                      </button>
                    </div>
                    <div className="form-text">
                      Supports CSV, XLSX, XLS files. Maximum file size: 10MB
                    </div>

                    <div className="mt-3 alert alert-warning">
                      <i className="bi bi-calendar me-2"></i>
                      <strong>Important:</strong> Punch time must be in <span className="text-danger">DD/MM/YYYY HH:MM:SS</span> format (e.g., 15/01/2024 09:00:00)
                    </div>
                  </div>
                </div>
              </div>

              {logsCsvData.length > 0 && (
                <div className="col-12">
                  <div className="card bg-light">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="card-title mb-0">
                          <i className="bi bi-table me-2"></i>
                          Preview ({logsCsvData.length} logs found)
                        </h6>
                        <span className="badge bg-warning">
                          <i className="bi bi-clock me-1"></i>
                          DD/MM/YYYY HH:MM:SS
                        </span>
                      </div>
                      <div className="table-responsive" style={{ maxHeight: '250px' }}>
                        <table className="table table-sm table-bordered">
                          <thead className="bg-light">
                            <tr>
                              {Object.keys(logsCsvData[0]).map(key => (
                                <th key={key} className="small">
                                  {key === 'punch_time' ? (
                                    <span className="text-danger">{key}*</span>
                                  ) : key === 'emp_code' ? (
                                    <span className="text-danger">{key}*</span>
                                  ) : (
                                    key
                                  )}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {logsCsvData.slice(0, 5).map((row, index) => (
                              <tr key={index}>
                                {Object.entries(row).map(([key, value], i) => (
                                  <td
                                    key={i}
                                    className="small"
                                    style={{
                                      color: key === 'punch_time' && !value ? 'red' : 'inherit',
                                      fontWeight: key === 'punch_time' ? 'bold' : 'normal'
                                    }}
                                  >
                                    {key === 'punch_time' && !value ? (
                                      <span className="text-danger">Required!</span>
                                    ) : (
                                      value || ''
                                    )}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {logsCsvData.length > 5 && (
                        <div className="text-center small text-muted mt-2">
                          <i className="bi bi-arrow-down"></i>
                          ... and {logsCsvData.length - 5} more logs
                        </div>
                      )}

                      <div className="mt-3 alert alert-light">
                        <div className="row">
                          <div className="col-md-6">
                            <small><strong>Punch Type Legend:</strong></small>
                            <div className="d-flex flex-wrap gap-1 mt-1">
                              <span className="badge bg-primary">0 = Check-in</span>
                              <span className="badge bg-danger">1 = Check-out</span>
                              <span className="badge bg-warning">2 = Break-out</span>
                              <span className="badge bg-success">3 = Break-in</span>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <small className="text-muted">
                              <i className="bi bi-info-circle me-1"></i>
                              Logs will be processed into attendance records automatically
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {isLogsImporting && (
                <div className="col-12">
                  <div className="card bg-light">
                    <div className="card-body">
                      <h6 className="card-title">
                        <i className="bi bi-arrow-repeat me-2"></i>
                        Processing Logs...
                      </h6>
                      <div className="progress mb-2">
                        <div
                          className="progress-bar progress-bar-striped progress-bar-animated bg-warning"
                          style={{ width: `${logsImportProgress}%` }}
                        >
                          {logsImportProgress}%
                        </div>
                      </div>
                      <small className="text-muted">
                        Processing raw attendance logs. This may take a moment...
                      </small>
                      <div className="mt-2">
                        <small className="text-muted">
                          <i className="bi bi-clock-history me-1"></i>
                          Logs are being converted to attendance records automatically
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClose}
              disabled={isLogsImporting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-outline-warning"
              onClick={downloadLogsTemplate}
              disabled={isLogsImporting}
            >
              <i className="bi bi-download me-2"></i>
              Get Template
            </button>
            <button
              type="button"
              className="btn btn-warning"
              onClick={handleLogsImport}
              disabled={!logsCsvData.length || isLogsImporting}
            >
              {isLogsImporting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Processing...
                </>
              ) : (
                <>
                  <i className="bi bi-device-hdd me-2"></i>
                  Import Logs
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportLogsModal;